import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Download, FileText, ExternalLink, Copy, Check, Award } from "lucide-react";

export default function ManagerReports() {
  const [data, setData] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [badge, setBadge] = useState(null);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    (async () => {
      const [a, c] = await Promise.all([
        api.get("/clinic/analytics"),
        api.get("/clinics/mine"),
      ]);
      setData(a.data); setClinic(c.data);
      if (c.data?.clinic_id) {
        try { const b = await api.get(`/badges/${c.data.clinic_id}`); setBadge(b.data); } catch (e) { void e; }
      }
    })();
  }, []);

  const exportCsv = (rows, name) => {
    if (!rows?.length) return;
    const keys = Object.keys(rows[0]);
    const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => r[k]).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  };

  const badgeUrl = clinic ? `${window.location.origin}/badge/${clinic.clinic_id}` : "";
  const embedCode = `<a href="${badgeUrl}" target="_blank" rel="noopener"><img src="${badgeUrl}.svg" alt="Sonote Clinic Wellness Index" /></a>`;

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <div>
      <span className="pill">Reports</span>
      <h1 className="mt-2 text-3xl font-bold">Wellness reports.</h1>
      <p className="text-zinc-500 mt-1">Export anonymized clinic data and share your public Wellness Index badge.</p>

      {/* Wellness Index Badge */}
      <section className="mt-8 card-elev" data-testid="badge-share">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5" style={{ color: "var(--gold)" }} />
              <h2 className="font-bold text-xl">Public Wellness Index badge</h2>
            </div>
            <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
              A public, anonymized monthly score recruiters and applicants can see. Embed it on your
              careers page or share the link to attract talent who care about workplace wellness.
            </p>
            <div className="mt-5 space-y-3">
              <div>
                <div className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1">Share link</div>
                <div className="flex gap-2">
                  <input readOnly value={badgeUrl} className="flex-1 h-11 px-3 rounded-md border border-zinc-200 bg-zinc-50 text-sm" data-testid="badge-share-url" />
                  <button onClick={() => copy(badgeUrl, "url")} className="btn-outline h-11 px-4 text-sm" data-testid="badge-copy-url">
                    {copied === "url" ? <><Check className="w-4 h-4 mr-1" />Copied</> : <><Copy className="w-4 h-4 mr-1" />Copy</>}
                  </button>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1">Embed snippet</div>
                <div className="flex gap-2">
                  <input readOnly value={embedCode} className="flex-1 h-11 px-3 rounded-md border border-zinc-200 bg-zinc-50 text-xs font-mono" data-testid="badge-embed" />
                  <button onClick={() => copy(embedCode, "embed")} className="btn-outline h-11 px-4 text-sm" data-testid="badge-copy-embed">
                    {copied === "embed" ? <><Check className="w-4 h-4 mr-1" />Copied</> : <><Copy className="w-4 h-4 mr-1" />Copy</>}
                  </button>
                </div>
              </div>
              <a href={badgeUrl} target="_blank" rel="noopener noreferrer" className="btn-gold inline-flex h-11 px-5 text-sm" data-testid="badge-preview-link">
                Preview public badge <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </div>
          </div>
          <div className="w-full sm:w-72 rounded-2xl overflow-hidden border border-zinc-200">
            {badge ? (
              <div className="p-6 text-white" style={{ background: badge.color }} data-testid="badge-preview">
                <div className="text-xs uppercase tracking-[0.15em] font-bold opacity-90">{badge.month}</div>
                {badge.score == null ? (
                  <div className="mt-3 font-extrabold text-2xl">Privacy-protected</div>
                ) : (
                  <>
                    <div className="mt-3 flex items-baseline gap-1">
                      <span className="text-5xl font-black tabular-nums">{badge.score}</span>
                      <span className="opacity-80 text-sm font-semibold">/100</span>
                    </div>
                    <div className="mt-1 font-bold">{badge.tier}</div>
                  </>
                )}
                <div className="mt-4 text-xs opacity-90">Sonote · {clinic?.name}</div>
              </div>
            ) : (
              <div className="p-6 bg-zinc-100 text-zinc-500 text-sm">Badge loading…</div>
            )}
          </div>
        </div>
      </section>

      <div className="grid md:grid-cols-2 gap-6 mt-8">
        <div className="card-elev">
          <FileText className="w-6 h-6" style={{ color: "var(--gold)" }} />
          <h3 className="mt-3 font-bold">Trend CSV (90d)</h3>
          <p className="text-sm text-zinc-600 mt-1">Daily average pain + entries volume across your clinic.</p>
          <button onClick={() => exportCsv(data?.trend, "sonote-trend.csv")} className="btn-gold mt-4" data-testid="report-export-trend">
            <Download className="w-4 h-4 mr-2" />Download
          </button>
        </div>
        <div className="card-elev">
          <FileText className="w-6 h-6" style={{ color: "var(--gold)" }} />
          <h3 className="mt-3 font-bold">Hotspots CSV</h3>
          <p className="text-sm text-zinc-600 mt-1">Pain reports grouped by anatomical region.</p>
          <button onClick={() => exportCsv(data?.hotspots, "sonote-hotspots.csv")} className="btn-gold mt-4" data-testid="report-export-hotspots">
            <Download className="w-4 h-4 mr-2" />Download
          </button>
        </div>
        <div className="card-elev md:col-span-2">
          <h3 className="font-bold">PDF Monthly Report</h3>
          <p className="text-sm text-zinc-600 mt-1">Print-ready monthly wellness brief with participation, hotspots and recommendations. PDF generation coming soon — use the CSV exports above for now.</p>
        </div>
      </div>
    </div>
  );
}
